/**
 * Platform Metrics - Submissions, offers, inquiries, and combined activity
 *
 * Route: /admin/reporting
 */

import { useState, useMemo, useCallback, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import clsx from 'clsx'
import { format, subMonths } from 'date-fns'
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  LabelList,
  Legend,
} from 'recharts'
import { Stack } from '@/common/components/stack'
import { Text } from '@/common/components/text'
import { Button } from '@/common/components/button'
import { Icon } from '@/common/components/icon'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { Select, type SelectOption } from '@/common/components/select'
import { Multiselect, type MultiselectOption } from '@/common/components/multiselect'
import { DatePicker } from '@/common/components/date-picker'
import { useAuth } from '@/common/hooks/useAuth'
import {
  useSubmissionVolume,
  useSubmissionActivityRate,
  useMarkets,
  useSubmissionBreakdown,
  useOfferBreakdown,
  useInquiryBreakdown,
  useSubmissionRecords,
  useOfferRecords,
  useInquiryRecords,
} from '@/features/reporting/hooks/useReportingData'
import {
  fetchSubmissionRecords,
  fetchOfferRecords,
  fetchInquiryRecords,
} from '@/features/reporting/api/reportingApi'
import { Tooltip as InfoTooltip } from '@/common/components/tooltip'
import { ExportMenu } from '@/features/reporting/components/ExportMenu'
import { incompleteOffset } from '@/features/reporting/utils/periodUtils'
import { ChartModal, ExpandChartButton } from '@/features/reporting/components/ChartModal'
import { series, chrome, semantic, table, tooltipStyle, tickProps } from '@/features/reporting/chartPalette'
import * as styles from './index.css'

export const Route = createFileRoute({
  component: PlatformMetricsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    granularity: (['month', 'week', 'day'].includes(search.granularity as string)
      ? (search.granularity as Granularity) : 'week'),
    dateRange: (['1y', '6mo', '3mo', '1mo', 'custom'].includes(search.dateRange as string)
      ? (search.dateRange as DateRangePreset) : '3mo'),
    market: (search.market as string) || '',
    metric: (['submissions', 'offers', 'inquiries', 'combined'].includes(search.metric as string)
      ? (search.metric as SelectedMetric) : 'submissions'),
  }),
})

// =============================================================================
// Types
// =============================================================================

type SelectedMetric = 'submissions' | 'offers' | 'inquiries' | 'combined'
type Granularity = 'month' | 'week' | 'day'
type DateRangePreset = '1y' | '6mo' | '3mo' | '1mo' | 'custom'

const PAGE_SIZE_OPTIONS: SelectOption[] = [
  { value: '25', label: '25' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
  { value: '200', label: '200' },
]

// =============================================================================
// Formatting Helpers
// =============================================================================

function formatCount(value: number): string {
  return value.toLocaleString('en-US')
}

function formatCurrency(value: number): string {
  return `$${value.toLocaleString('en-US')}`
}

function calcDelta(current: number, prior: number | undefined): number | null {
  if (prior === undefined || prior === 0) return null
  return ((current - prior) / prior) * 100
}

function deltaColor(delta: number | null): string {
  if (delta === null) return semantic.neutral
  if (delta > 0) return semantic.positive
  if (delta < 0) return semantic.negative
  return semantic.neutral
}

function deltaLabel(delta: number | null, granLabel: string): string {
  if (delta === null) return `no prior ${granLabel}`
  if (delta === 0) return `0.0%`
  const sign = delta > 0 ? '+' : ''
  return `${sign}${delta.toFixed(1)}%`
}

function deltaIndicator(delta: number | null): string {
  if (delta === null) return '\u2014 '
  if (delta > 0) return '\u25B2 '
  if (delta < 0) return '\u25BC '
  return '= '
}

/** Parse a yyyy-MM-dd string as a local-time Date (avoids UTC midnight timezone shift) */
function parseDateAsLocal(dateStr: string): Date {
  const [y, m, d] = dateStr.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/** Convert API period strings to readable date labels. */
function formatPeriodLabel(period: string): string {
  const weekMatch = period.match(/^(\d{4})-W(\d{2})$/)
  if (weekMatch) {
    const year = Number(weekMatch[1])
    const week = Number(weekMatch[2])
    const jan4 = new Date(year, 0, 4)
    const dayOfWeek = jan4.getDay() || 7
    const monday = new Date(jan4)
    monday.setDate(jan4.getDate() - dayOfWeek + 1 + (week - 1) * 7)
    return format(monday, 'MMM d')
  }
  const monthMatch = period.match(/^(\d{4})-(\d{2})$/)
  if (monthMatch) {
    const d = new Date(Number(monthMatch[1]), Number(monthMatch[2]) - 1, 1)
    return format(d, "MMM ''yy")
  }
  const dayMatch = period.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (dayMatch) {
    const d = new Date(Number(dayMatch[1]), Number(dayMatch[2]) - 1, Number(dayMatch[3]))
    return format(d, 'MMM d')
  }
  return period
}

/** Sum all periods in the date range (including current incomplete period) */
function sumAllPeriods(data: Array<{ period: string; value: number }> | undefined): number {
  if (!data || data.length === 0) return 0
  return data.reduce((sum, d) => sum + d.value, 0)
}

// =============================================================================
// Breakdown label component showing value + % of total
// =============================================================================

function BreakdownValueLabel(props: { x?: string | number; y?: string | number; width?: string | number; height?: string | number; value?: string | number | boolean | null; total: number }) {
  const x = typeof props.x === 'number' ? props.x : 0
  const y = typeof props.y === 'number' ? props.y : 0
  const width = typeof props.width === 'number' ? props.width : 0
  const height = typeof props.height === 'number' ? props.height : 0
  const value = typeof props.value === 'number' ? props.value : 0
  const { total } = props
  const pct = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0'
  return (
    <text x={x + width + 6} y={y + height / 2 + 4} fontSize={12} fill={table.headerText}>
      {value.toLocaleString()} ({pct}%)
    </text>
  )
}

// =============================================================================
// Main Component
// =============================================================================

function PlatformMetricsPage() {
  const { token } = useAuth()
  const searchParams = Route.useSearch()
  const { granularity, dateRange, market, metric: selectedMetric } = searchParams
  const navigate = useNavigate()

  const updateSearch = useCallback(
    (updates: Partial<typeof searchParams>) => {
      void navigate({ to: '.', search: { ...searchParams, ...updates }, replace: true })
    },
    [navigate, searchParams]
  )

  const setGranularity = useCallback((val: Granularity) => updateSearch({ granularity: val }), [updateSearch])
  const setMarket = useCallback((vals: string[]) => updateSearch({ market: vals.join(',') }), [updateSearch])
  const marketParam = market || undefined
  const setDateRange = useCallback((val: DateRangePreset) => updateSearch({ dateRange: val }), [updateSearch])
  const setSelectedMetric = useCallback((val: SelectedMetric) => updateSearch({ metric: val }), [updateSearch])

  const [drillDownPeriod, setDrillDownPeriod] = useState<string | null>(null)
  const [drillDownPage, setDrillDownPage] = useState(1)
  const [inquiryDrillDownPage, setInquiryDrillDownPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [customStart, setCustomStart] = useState<Date | null>(null)
  const [customEnd, setCustomEnd] = useState<Date | null>(null)
  const [chartExpanded, setChartExpanded] = useState(false)

  const { startDateStr, endDateStr } = useMemo(() => {
    const now = new Date()
    if (dateRange === 'custom') {
      return {
        startDateStr: format(customStart ?? subMonths(now, 12), 'yyyy-MM-dd'),
        endDateStr: format(customEnd ?? now, 'yyyy-MM-dd'),
      }
    }
    let start: Date
    switch (dateRange) {
      case '1y': start = subMonths(now, 12); break
      case '6mo': start = subMonths(now, 6); break
      case '3mo': start = subMonths(now, 3); break
      case '1mo': start = subMonths(now, 1); break
    }
    return { startDateStr: format(start, 'yyyy-MM-dd'), endDateStr: format(now, 'yyyy-MM-dd') }
  }, [dateRange, customStart, customEnd])

  useEffect(() => {
    setDrillDownPeriod(null)
  }, [granularity, market, dateRange, selectedMetric])

  useEffect(() => {
    setDrillDownPage(1)
    setInquiryDrillDownPage(1)
  }, [drillDownPeriod])

  useEffect(() => {
    setDrillDownPage(1)
    setInquiryDrillDownPage(1)
  }, [pageSize])

  // ---------------------------------------------------------------------------
  // Data Hooks
  // ---------------------------------------------------------------------------
  const { data: submissionRes } = useSubmissionVolume(granularity, marketParam, startDateStr, endDateStr)
  const { data: activityRes } = useSubmissionActivityRate(granularity, marketParam, startDateStr, endDateStr)
  const { data: marketsRes } = useMarkets()

  // Drill-down hooks — only fire for the selected metric
  const { data: submissionBreakdownRes } = useSubmissionBreakdown(
    selectedMetric === 'submissions' ? drillDownPeriod : null,
    granularity, marketParam, startDateStr, endDateStr
  )
  const { data: offerBreakdownRes } = useOfferBreakdown(
    selectedMetric === 'offers' || selectedMetric === 'combined' ? drillDownPeriod : null,
    granularity, marketParam, startDateStr, endDateStr
  )
  const { data: inquiryBreakdownRes } = useInquiryBreakdown(
    selectedMetric === 'inquiries' || selectedMetric === 'combined' ? drillDownPeriod : null,
    granularity, marketParam, startDateStr, endDateStr
  )

  // For combined, merge offer + inquiry breakdowns by market label
  const combinedBreakdownRes = useMemo(() => {
    if (selectedMetric !== 'combined' || !offerBreakdownRes || !inquiryBreakdownRes) return null
    const map = new Map<string, number>()
    for (const d of offerBreakdownRes.data ?? []) map.set(d.label, (map.get(d.label) ?? 0) + d.value)
    for (const d of inquiryBreakdownRes.data ?? []) map.set(d.label, (map.get(d.label) ?? 0) + d.value)
    return { data: Array.from(map, ([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value) }
  }, [selectedMetric, offerBreakdownRes, inquiryBreakdownRes])

  const breakdownRes = selectedMetric === 'submissions'
    ? submissionBreakdownRes
    : selectedMetric === 'combined' ? combinedBreakdownRes
    : selectedMetric === 'inquiries' ? inquiryBreakdownRes : offerBreakdownRes

  const { data: submissionRecordsRes, isFetching: isSubRecordsFetching } = useSubmissionRecords(
    selectedMetric === 'submissions' ? drillDownPeriod : null,
    granularity, marketParam, startDateStr, endDateStr, drillDownPage, pageSize
  )
  const { data: offerRecordsRes, isFetching: isOfferRecordsFetching } = useOfferRecords(
    selectedMetric === 'offers' || selectedMetric === 'combined' ? drillDownPeriod : null,
    granularity, marketParam, startDateStr, endDateStr, drillDownPage, pageSize
  )
  const { data: inquiryRecordsRes, isFetching: isInquiryRecordsFetching } = useInquiryRecords(
    selectedMetric === 'inquiries' || selectedMetric === 'combined' ? drillDownPeriod : null,
    granularity, marketParam, startDateStr, endDateStr,
    selectedMetric === 'combined' ? inquiryDrillDownPage : drillDownPage, pageSize
  )

  const recordsRes = selectedMetric === 'submissions'
    ? submissionRecordsRes
    : selectedMetric === 'inquiries' ? inquiryRecordsRes : offerRecordsRes
  const isRecordsFetching = selectedMetric === 'submissions'
    ? isSubRecordsFetching
    : selectedMetric === 'combined'
      ? isOfferRecordsFetching || isInquiryRecordsFetching
      : selectedMetric === 'inquiries' ? isInquiryRecordsFetching : isOfferRecordsFetching

  // ---------------------------------------------------------------------------
  // Derived Data
  // ---------------------------------------------------------------------------
  const submissionData = submissionRes?.data
  const activityData = activityRes?.data

  const marketOptions: MultiselectOption[] = useMemo(
    () => (marketsRes?.data ?? []).map((m) => ({ value: m, label: m })),
    [marketsRes?.data]
  )

  const submissionChartData = useMemo(
    () => (submissionData ?? []).map((d) => ({ period: d.period, value: d.value })).sort((a, b) => a.period.localeCompare(b.period)),
    [submissionData]
  )

  const dualAxisChartData = useMemo(
    () => [...(activityData ?? [])].sort((a, b) => a.period.localeCompare(b.period)),
    [activityData]
  )

  const granularityLabel = granularity === 'month' ? 'month' : granularity === 'week' ? 'week' : 'day'
  const granularityLabelCap = granularity === 'month' ? 'Monthly' : granularity === 'week' ? 'Weekly' : 'Daily'

  // Submission card values — skip trailing incomplete period if present
  const subOff = incompleteOffset(submissionChartData, granularity)
  const subLatest = submissionChartData.length >= (1 + subOff) ? submissionChartData.at(-1 - subOff)!.value : 0
  const subPrior = submissionChartData.length >= (2 + subOff) ? submissionChartData.at(-2 - subOff)!.value : undefined
  const subDelta = calcDelta(subLatest, subPrior)

  // Activity rate card values — skip trailing incomplete period if present
  // Sort activityData so incompleteOffset and .at() indexing work correctly
  const ad = useMemo(
    () => activityData ? [...activityData].sort((a, b) => a.period.localeCompare(b.period)) : undefined,
    [activityData]
  )
  const adOff = incompleteOffset(ad, granularity)
  const adLen = ad?.length ?? 0
  // Totals across full date range (including current incomplete period)
  const adAll = ad ?? []
  const offerTotal = adAll.reduce((s, d) => s + d.offer_count, 0)
  const offerLatest = adLen >= (1 + adOff) ? ad!.at(-1 - adOff)!.offer_count : 0
  const offerPrior = adLen >= (2 + adOff) ? ad!.at(-2 - adOff)!.offer_count : undefined
  const offerDelta = calcDelta(offerLatest, offerPrior)

  const inquiryTotal = adAll.reduce((s, d) => s + d.inquiry_count, 0)
  const inquiryLatest = adLen >= (1 + adOff) ? ad!.at(-1 - adOff)!.inquiry_count : 0
  const inquiryPrior = adLen >= (2 + adOff) ? ad!.at(-2 - adOff)!.inquiry_count : undefined
  const inquiryDelta = calcDelta(inquiryLatest, inquiryPrior)

  const combinedTotal = adAll.reduce((s, d) => s + d.combined_count, 0)
  const combinedLatest = adLen >= (1 + adOff) ? ad!.at(-1 - adOff)!.combined_count : 0
  const combinedPrior = adLen >= (2 + adOff) ? ad!.at(-2 - adOff)!.combined_count : undefined
  const combinedDelta = calcDelta(combinedLatest, combinedPrior)

  // Metric cards definition
  const metricCards: Array<{
    key: SelectedMetric
    label: string
    definition: string
    totalValue: string
    totalTooltip: string
    recentValue: string
    delta: number | null
    showTotal: boolean
  }> = useMemo(() => [
    {
      key: 'submissions' as const,
      label: 'Property Submissions',
      definition: 'The total number of properties submitted to the Cash Offer Marketplace.',
      totalValue: formatCount(sumAllPeriods(submissionChartData)),
      totalTooltip: 'Total property submissions across the selected time period',
      recentValue: formatCount(subLatest),
      delta: subDelta,
      showTotal: true,
    },
    {
      key: 'offers' as const,
      label: 'Cash Offers',
      definition: 'The total number of cash offers submitted by investors.',
      totalValue: formatCount(offerTotal),
      totalTooltip: 'Total cash offers across the selected time period',
      recentValue: formatCount(offerLatest),
      delta: offerDelta,
      showTotal: true,
    },
    {
      key: 'inquiries' as const,
      label: 'Inquiries',
      definition: 'The total number of information requests (investor inquiries about properties).',
      totalValue: formatCount(inquiryTotal),
      totalTooltip: 'Total investor inquiries across the selected time period',
      recentValue: formatCount(inquiryLatest),
      delta: inquiryDelta,
      showTotal: true,
    },
    {
      key: 'combined' as const,
      label: 'All Offers/Inquiries',
      definition: 'The combined total of cash offers and investor inquiries.',
      totalValue: formatCount(combinedTotal),
      totalTooltip: 'Total offers + inquiries across the selected time period',
      recentValue: formatCount(combinedLatest),
      delta: combinedDelta,
      showTotal: true,
    },
  ], [submissionChartData, subLatest, subDelta, offerTotal, offerLatest, offerDelta, inquiryTotal, inquiryLatest, inquiryDelta, combinedTotal, combinedLatest, combinedDelta])

  // Chart config per metric — bar dataKey, line dataKey, labels
  const chartConfig = useMemo(() => {
    switch (selectedMetric) {
      case 'submissions':
        return { title: 'Submission Volume', barKey: 'value', lineKey: null, barLabel: 'Submissions', lineLabel: '' }
      case 'offers':
        return { title: 'Offers', barKey: 'offer_count', lineKey: 'avg_offers_per_submission', barLabel: 'Offer Count', lineLabel: 'Avg Offers/Submission' }
      case 'inquiries':
        return { title: 'Inquiries', barKey: 'inquiry_count', lineKey: 'avg_inquiries_per_submission', barLabel: 'Inquiry Count', lineLabel: 'Avg Inquiries/Submission' }
      case 'combined':
        return { title: 'All Offers/Inquiries', barKey: 'combined_count', lineKey: 'avg_combined_per_submission', barLabel: 'All Offers/Inquiries', lineLabel: 'Avg/Submission' }
    }
  }, [selectedMetric])

  const drillDownLabel = selectedMetric === 'submissions' ? 'Submissions'
    : selectedMetric === 'offers' ? 'Offers'
    : selectedMetric === 'inquiries' ? 'Inquiries'
    : 'All Offers/Inquiries'

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------
  const handleChartClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (nextState: any) => {
      if (nextState?.activeLabel != null) {
        setDrillDownPeriod(String(nextState.activeLabel))
      }
    },
    []
  )

  const handleCloseDrillDown = useCallback(() => {
    setDrillDownPeriod(null)
  }, [])

  const handleDateRangeChange = useCallback((preset: DateRangePreset) => {
    if (preset === 'custom' && dateRange !== 'custom') {
      const now = new Date()
      const starts: Record<Exclude<DateRangePreset, 'custom'>, Date> = {
        '1y': subMonths(now, 12), '6mo': subMonths(now, 6), '3mo': subMonths(now, 3),
        '1mo': subMonths(now, 1),
      }
      setCustomStart(starts[dateRange])
      setCustomEnd(now)
    }
    setDateRange(preset)
  }, [dateRange, setDateRange])

  // Breakdown totals for % labels
  const breakdownTotal = useMemo(
    () => (breakdownRes?.data ?? []).reduce((sum, d) => sum + d.value, 0),
    [breakdownRes?.data]
  )

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.controls}>
            <div className={styles.controlSelect}>
              <Multiselect label="Market" options={marketOptions} value={market ? market.split(',') : []} onValueChange={setMarket} placeholder="All Markets" size="sm" />
            </div>
            <div className={styles.granularityToggle}>
              <button className={clsx(styles.granularityButton, granularity === 'month' && styles.granularityButtonActive)} onClick={() => setGranularity('month')}>Monthly</button>
              <button className={clsx(styles.granularityButton, granularity === 'week' && styles.granularityButtonActive)} onClick={() => setGranularity('week')}>Weekly</button>
              <button className={clsx(styles.granularityButton, granularity === 'day' && styles.granularityButtonActive)} onClick={() => setGranularity('day')}>Daily</button>
            </div>
            <div className={styles.controlDateGroup}>
              <div className={styles.granularityToggle}>
                {(['1y', '6mo', '3mo', '1mo', 'custom'] as const).map((preset) => (
                  <button key={preset} className={clsx(styles.granularityButton, dateRange === preset && styles.granularityButtonActive)} onClick={() => handleDateRangeChange(preset)}>
                    {preset === 'custom' ? 'Custom' : preset}
                  </button>
                ))}
              </div>
              <div className={styles.customDateRange}>
                <div className={styles.customDatePicker}>
                  <DatePicker label="Start" value={parseDateAsLocal(startDateStr)} onChange={setCustomStart} maxDate={parseDateAsLocal(endDateStr)} size="sm" disabled={dateRange !== 'custom'} />
                </div>
                <Text variant="bodySm" color="muted">{'\u2013'}</Text>
                <div className={styles.customDatePicker}>
                  <DatePicker label="End" value={parseDateAsLocal(endDateStr)} onChange={setCustomEnd} minDate={parseDateAsLocal(startDateStr)} maxDate={new Date()} size="sm" disabled={dateRange !== 'custom'} />
                </div>
              </div>
            </div>
          </div>

        {/* 4 Clickable Metric Cards */}
        <InfoTooltip.Provider>
        <div className={styles.metricsGrid}>
          {metricCards.map((card) => (
            <div
              key={card.key}
              className={clsx(styles.metricCard, selectedMetric === card.key && styles.metricCardActive)}
              onClick={() => setSelectedMetric(card.key)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setSelectedMetric(card.key)
                }
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Text variant="bodySm" color="muted">{card.label}</Text>
                <InfoTooltip content={card.definition} side="top" delayDuration={200}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', color: '#9ca3af', cursor: 'help', flexShrink: 0 }}>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                      <path d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm0 1.5a5.5 5.5 0 1 1 0 11 5.5 5.5 0 0 1 0-11zm.75 7.75v-4h-1.5v4h1.5zM8 5.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5z"/>
                    </svg>
                  </span>
                </InfoTooltip>
              </div>
              <Text as="h4" variant="h4" weight="bold" style={{ margin: '4px 0' }} title={card.totalTooltip}>
                {card.totalValue}
              </Text>
              {card.showTotal && (
                <Text variant="bodyXs" color="muted" style={{ marginBottom: '4px' }}>
                  total in period
                </Text>
              )}
              <div className={styles.metricCardSecondary}>
                <Text variant="bodyXs" color="muted">
                  Most recent completed {granularityLabel}:{' '}
                  <strong>{card.recentValue}</strong>
                </Text>
                <Text variant="bodyXs" style={{ color: deltaColor(card.delta) }}>
                  {deltaIndicator(card.delta)}
                  {card.delta !== null
                    ? `${deltaLabel(card.delta, granularityLabel)} vs prior ${granularityLabel}`
                    : deltaLabel(card.delta, granularityLabel)}
                </Text>
              </div>
            </div>
          ))}
        </div>
        </InfoTooltip.Provider>

        {/* Single Chart (switches based on selected metric) */}
        <div className={styles.chartContainer}>
          <div className={styles.chartHeader}>
            <Text variant="bodySm" color="muted">
              {chartConfig.title} ({granularityLabelCap})
            </Text>
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              <ExpandChartButton onClick={() => setChartExpanded(true)} />
              <ExportMenu options={[
                { label: 'Chart Data (CSV)', data: (selectedMetric === 'submissions' ? submissionChartData : dualAxisChartData) as unknown as Record<string, unknown>[], filename: `${selectedMetric}-trend.csv` },
              ]} />
            </div>
          </div>

          {selectedMetric === 'submissions' ? (
            /* Submissions: simple bar chart */
            (<ResponsiveContainer width="100%" height={280}>
              <BarChart data={submissionChartData} onClick={handleChartClick}>
                <CartesianGrid strokeDasharray="3 3" stroke={chrome.gridStroke} />
                <XAxis dataKey="period" tick={tickProps} tickLine={false} axisLine={{ stroke: chrome.axisLine }} tickFormatter={formatPeriodLabel} />
                <YAxis tick={tickProps} tickLine={false} axisLine={false} width={60} domain={[0, (max: number) => Math.ceil(max * 1.15) || 1]} />
                <Tooltip isAnimationActive={false} contentStyle={tooltipStyle} labelFormatter={(label) => formatPeriodLabel(String(label))} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="value" name="Submissions" fill={series.primary} radius={[4, 4, 0, 0]} isAnimationActive={false} cursor="pointer">
                  <LabelList
                    dataKey="value"
                    position="top"
                    style={{ fontSize: '12px', fontWeight: 600, fill: series.primary }}
                    formatter={(v: unknown) => typeof v === 'number' ? v.toLocaleString() : ''}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>)
          ) : (
            /* Offers / Inquiries / Combined: dual-axis bar + line */
            (<ResponsiveContainer width="100%" height={280}>
              <ComposedChart data={dualAxisChartData} onClick={handleChartClick}>
                <CartesianGrid strokeDasharray="3 3" stroke={chrome.gridStroke} />
                <XAxis dataKey="period" tick={tickProps} tickLine={false} axisLine={{ stroke: chrome.axisLine }} tickFormatter={formatPeriodLabel} />
                <YAxis yAxisId="left" tick={tickProps} tickLine={false} axisLine={false} width={60} domain={[0, (max: number) => Math.ceil(max * 1.15) || 1]} />
                <YAxis yAxisId="right" orientation="right" tick={tickProps} tickLine={false} axisLine={false} width={60} domain={[0, (max: number) => Math.ceil(max * 1.15) || 1]} />
                <Tooltip isAnimationActive={false} contentStyle={tooltipStyle} labelFormatter={(label) => formatPeriodLabel(String(label))} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar yAxisId="left" dataKey={chartConfig.barKey} name={chartConfig.barLabel} fill={series.primary} radius={[4, 4, 0, 0]} isAnimationActive={false} cursor="pointer">
                  <LabelList
                    dataKey={chartConfig.barKey}
                    position="top"
                    style={{ fontSize: '12px', fontWeight: 600, fill: series.primary }}
                    formatter={(v: unknown) => typeof v === 'number' ? v.toLocaleString() : ''}
                  />
                </Bar>
                <Line yAxisId="right" type="monotone" dataKey={chartConfig.lineKey!} name={chartConfig.lineLabel} stroke={series.accent1} strokeWidth={2} dot={{ r: 3, fill: series.accent1 }} isAnimationActive={false}>
                  <LabelList
                    dataKey={chartConfig.lineKey!}
                    position="top"
                    style={{ fontSize: '11px', fontWeight: 600, fill: series.accent1 }}
                    formatter={(v: unknown) => typeof v === 'number' ? v.toFixed(2) : ''}
                  />
                </Line>
              </ComposedChart>
            </ResponsiveContainer>)
          )}
        </div>

        <ChartModal
          open={chartExpanded}
          onClose={() => setChartExpanded(false)}
          title={`${chartConfig.title} (${granularityLabelCap})`}
        >
          {selectedMetric === 'submissions' ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={submissionChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={chrome.gridStroke} />
                <XAxis dataKey="period" tick={tickProps} tickLine={false} axisLine={{ stroke: chrome.axisLine }} tickFormatter={formatPeriodLabel} />
                <YAxis tick={tickProps} tickLine={false} axisLine={false} width={60} domain={[0, (max: number) => Math.ceil(max * 1.15) || 1]} />
                <Tooltip isAnimationActive={false} contentStyle={tooltipStyle} labelFormatter={(label) => formatPeriodLabel(String(label))} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="value" name="Submissions" fill={series.primary} radius={[4, 4, 0, 0]} isAnimationActive={false}>
                  <LabelList dataKey="value" position="top" style={{ fontSize: '12px', fontWeight: 600, fill: series.primary }} formatter={(v: unknown) => typeof v === 'number' ? v.toLocaleString() : ''} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={dualAxisChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={chrome.gridStroke} />
                <XAxis dataKey="period" tick={tickProps} tickLine={false} axisLine={{ stroke: chrome.axisLine }} tickFormatter={formatPeriodLabel} />
                <YAxis yAxisId="left" tick={tickProps} tickLine={false} axisLine={false} width={60} domain={[0, (max: number) => Math.ceil(max * 1.15) || 1]} />
                <YAxis yAxisId="right" orientation="right" tick={tickProps} tickLine={false} axisLine={false} width={60} domain={[0, (max: number) => Math.ceil(max * 1.15) || 1]} />
                <Tooltip isAnimationActive={false} contentStyle={tooltipStyle} labelFormatter={(label) => formatPeriodLabel(String(label))} />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar yAxisId="left" dataKey={chartConfig.barKey} name={chartConfig.barLabel} fill={series.primary} radius={[4, 4, 0, 0]} isAnimationActive={false}>
                  <LabelList dataKey={chartConfig.barKey} position="top" style={{ fontSize: '12px', fontWeight: 600, fill: series.primary }} formatter={(v: unknown) => typeof v === 'number' ? v.toLocaleString() : ''} />
                </Bar>
                <Line yAxisId="right" type="monotone" dataKey={chartConfig.lineKey!} name={chartConfig.lineLabel} stroke={series.accent1} strokeWidth={2} dot={{ r: 3, fill: series.accent1 }} isAnimationActive={false}>
                  <LabelList
                    dataKey={chartConfig.lineKey!}
                    position="top"
                    style={{ fontSize: '11px', fontWeight: 600, fill: series.accent1 }}
                    formatter={(v: unknown) => typeof v === 'number' ? v.toFixed(2) : ''}
                  />
                </Line>
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </ChartModal>

        {/* Drill-Down Panel */}
        {drillDownPeriod && (
          <div className={styles.drillDownPanel}>
            <div className={styles.drillDownHeader}>
              <Text variant="body" weight="bold">Breakdown: {formatPeriodLabel(drillDownPeriod)} — {drillDownLabel}</Text>
              <Button variant="ghost" size="sm" onClick={handleCloseDrillDown}>
                <Icon name="close" size={18} />
              </Button>
            </div>

            {/* Breakdown Chart */}
            <div style={{ position: 'relative', minHeight: '200px', marginBottom: '24px' }}>
              <div className={styles.chartHeader}>
                <div>
                  <Text variant="bodySm" color="muted">
                    {drillDownLabel} by Market
                  </Text>
                  {breakdownTotal > 0 && (
                    <Text variant="bodyXs" color="muted" style={{ marginTop: '2px' }}>
                      Total: <strong>{breakdownTotal.toLocaleString()}</strong>
                    </Text>
                  )}
                </div>
                <ExportMenu options={[
                  { label: 'Breakdown (CSV)', data: (breakdownRes?.data ?? []) as unknown as Record<string, unknown>[], filename: `${drillDownLabel.toLowerCase()}-breakdown-${drillDownPeriod}.csv` },
                ]} />
              </div>
              {!breakdownRes && (
                <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.6)', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px' }}>
                  <LoadingSpinner size="sm" />
                </div>
              )}
              {breakdownRes?.data && breakdownRes.data.length > 0 && (
                <ResponsiveContainer width="100%" height={Math.max(160, breakdownRes.data.length * 40)} style={{ paddingRight: '120px' }}>
                  <BarChart data={breakdownRes.data} layout="vertical" margin={{ right: 120 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chrome.gridStroke} horizontal={false} />
                    <XAxis type="number" tick={tickProps} tickLine={false} axisLine={{ stroke: chrome.axisLine }} />
                    <YAxis type="category" dataKey="label" tick={tickProps} tickLine={false} axisLine={false} width={120} />
                    <Tooltip isAnimationActive={false} contentStyle={tooltipStyle} />
                    <Bar dataKey="value" fill={series.primary} radius={[0, 4, 4, 0]} isAnimationActive={false}>
                      <LabelList
                        dataKey="value"
                        position="right"
                        content={(props) => (
                          <BreakdownValueLabel {...props} total={breakdownTotal} />
                        )}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>

            {/* Records Table */}
            <div style={{ position: 'relative', minHeight: '300px' }}>
              {(!recordsRes || isRecordsFetching) && (
                <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(255,255,255,0.6)', zIndex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px' }}>
                  <LoadingSpinner size="sm" />
                </div>
              )}
              {selectedMetric === 'combined' ? (
                /* Combined: show both offers and inquiries tables */
                (<>
                  {offerRecordsRes?.data && offerRecordsRes.data.length > 0 && (
                    <>
                    <div className={styles.chartHeader}>
                      <Text variant="bodySm" weight="bold">
                        Offers
                        {offerRecordsRes.pagination && (
                          <Text as="span" variant="bodyXs" color="muted" style={{ marginLeft: "8px", fontWeight: 400 }}>
                            ({offerRecordsRes.pagination.total_count.toLocaleString()} total)
                          </Text>
                        )}
                      </Text>
                      <Stack direction="row" gap="8" align="center">
                        <div className={styles.pageSizeSelect}>
                          <Select label="" size="sm" options={PAGE_SIZE_OPTIONS} value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setDrillDownPage(1) }} showOptional={false} />
                        </div>
                        <Text variant="bodyXs" color="muted">per page</Text>
                        <ExportMenu options={[
                          { label: 'Export Page', data: (offerRecordsRes?.data ?? []) as unknown as Record<string, unknown>[], filename: `offers-records-${drillDownPeriod}-page${drillDownPage}.csv` },
                        ]} />
                      </Stack>
                    </div>
                    <table className={styles.recordsTable}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'right', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px', width: '40px' }}>#</th>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Property</th>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Investor</th>
                          <th style={{ textAlign: 'right', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Offer Price</th>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {offerRecordsRes.data.map((record, idx) => (
                          <tr key={record.id}>
                            <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, textAlign: 'right', color: table.headerText, fontSize: '12px' }}>{(drillDownPage - 1) * pageSize + idx + 1}</td>
                            <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}` }}>{record.property_address ?? '-'}</td>
                            <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, color: table.headerText }}>{record.investor_name ?? '-'}</td>
                            <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, textAlign: 'right' }}>{formatCurrency(record.offer_price)}</td>
                            <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, textTransform: 'capitalize' }}>{record.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {offerRecordsRes.pagination && (
                      <div className={styles.paginationControls}>
                        <Text variant="bodyXs" color="muted">
                          Rows {((drillDownPage - 1) * pageSize + 1).toLocaleString()}–{Math.min(drillDownPage * pageSize, offerRecordsRes.pagination.total_count).toLocaleString()} of {offerRecordsRes.pagination.total_count.toLocaleString()}
                        </Text>
                        <Stack direction="row" gap="8">
                          <Button variant="outline" size="sm" disabled={isOfferRecordsFetching || offerRecordsRes.pagination.page <= 1} onClick={() => setDrillDownPage((p) => Math.max(1, p - 1))}>Previous</Button>
                          <Button variant="outline" size="sm" disabled={isOfferRecordsFetching || !offerRecordsRes.pagination.has_more} onClick={() => setDrillDownPage((p) => p + 1)}>Next</Button>
                        </Stack>
                      </div>
                    )}
                    </>
                  )}
                  {inquiryRecordsRes?.data && inquiryRecordsRes.data.length > 0 && (
                    <>
                    <div className={styles.chartHeader} style={{ marginTop: '24px' }}>
                      <Text variant="bodySm" weight="bold">
                        Inquiries
                        {inquiryRecordsRes.pagination && (
                          <Text as="span" variant="bodyXs" color="muted" style={{ marginLeft: "8px", fontWeight: 400 }}>
                            ({inquiryRecordsRes.pagination.total_count.toLocaleString()} total)
                          </Text>
                        )}
                      </Text>
                      <ExportMenu options={[
                        { label: 'Export Page', data: (inquiryRecordsRes?.data ?? []) as unknown as Record<string, unknown>[], filename: `inquiries-records-${drillDownPeriod}-page${drillDownPage}.csv` },
                      ]} />
                    </div>
                    <table className={styles.recordsTable}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'right', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px', width: '40px' }}>#</th>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Property</th>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Investor</th>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Message</th>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inquiryRecordsRes.data.map((record, idx) => (
                          <tr key={record.id}>
                            <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, textAlign: 'right', color: table.headerText, fontSize: '12px' }}>{(inquiryDrillDownPage - 1) * pageSize + idx + 1}</td>
                            <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}` }}>{record.property_address ?? '-'}</td>
                            <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, color: table.headerText }}>{record.investor_name ?? '-'}</td>
                            <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{record.message}</td>
                            <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, color: table.headerText }}>{new Date(record.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {inquiryRecordsRes.pagination && (
                      <div className={styles.paginationControls}>
                        <Text variant="bodyXs" color="muted">
                          Rows {((inquiryDrillDownPage - 1) * pageSize + 1).toLocaleString()}–{Math.min(inquiryDrillDownPage * pageSize, inquiryRecordsRes.pagination.total_count).toLocaleString()} of {inquiryRecordsRes.pagination.total_count.toLocaleString()}
                        </Text>
                        <Stack direction="row" gap="8">
                          <Button variant="outline" size="sm" disabled={isInquiryRecordsFetching || inquiryRecordsRes.pagination.page <= 1} onClick={() => setInquiryDrillDownPage((p) => Math.max(1, p - 1))}>Previous</Button>
                          <Button variant="outline" size="sm" disabled={isInquiryRecordsFetching || !inquiryRecordsRes.pagination.has_more} onClick={() => setInquiryDrillDownPage((p) => p + 1)}>Next</Button>
                        </Stack>
                      </div>
                    )}
                    </>
                  )}
                </>)
              ) : recordsRes?.data && recordsRes.data.length > 0 && (
                <>
                <div className={styles.chartHeader}>
                  <Text variant="bodySm" weight="bold">
                    Records
                    {recordsRes.pagination && (
                      <Text as="span" variant="bodyXs" color="muted" style={{ marginLeft: '8px', fontWeight: 400 }}>
                        ({recordsRes.pagination.total_count.toLocaleString()} total)
                      </Text>
                    )}
                  </Text>
                  <Stack direction="row" gap="8" align="center">
                    <div className={styles.pageSizeSelect}>
                      <Select
                        label=""
                        size="sm"
                        options={PAGE_SIZE_OPTIONS}
                        value={String(pageSize)}
                        onValueChange={(v) => { setPageSize(Number(v)); setDrillDownPage(1) }}
                        showOptional={false}
                      />
                    </div>
                    <Text variant="bodyXs" color="muted">per page</Text>
                    <ExportMenu options={[
                      { label: 'Export Page', data: (recordsRes?.data ?? []) as unknown as Record<string, unknown>[], filename: `${drillDownLabel.toLowerCase()}-records-${drillDownPeriod}-page${drillDownPage}.csv` },
                      ...(recordsRes?.pagination && recordsRes.pagination.total_pages > 1 ? [{
                        label: 'Export All Records',
                        description: `${recordsRes.pagination.total_count.toLocaleString()} records \u2014 may take a moment`,
                        fetchData: async () => {
                          if (!token || !drillDownPeriod) return []
                          const fetchFn = selectedMetric === 'submissions' ? fetchSubmissionRecords
                            : selectedMetric === 'inquiries' ? fetchInquiryRecords : fetchOfferRecords
                          const allData: Record<string, unknown>[] = []
                          const totalPages = recordsRes.pagination!.total_pages
                          for (let p = 1; p <= totalPages; p++) {
                            const res = await fetchFn(token, { period: drillDownPeriod, granularity, market: marketParam, start_date: startDateStr, end_date: endDateStr, page: p, page_size: pageSize })
                            allData.push(...(res.data as unknown as Record<string, unknown>[]))
                          }
                          return allData
                        },
                        filename: `${drillDownLabel.toLowerCase()}-records-${drillDownPeriod}-all.csv`,
                      }] : []),
                    ]} />
                  </Stack>
                </div>
                <table className={styles.recordsTable}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'right', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px', width: '40px' }}>#</th>
                      {selectedMetric === 'submissions' ? (
                        <>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Address</th>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Market</th>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Status</th>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Submitted</th>
                        </>
                      ) : selectedMetric === 'inquiries' ? (
                        <>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Property</th>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Investor</th>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Message</th>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Date</th>
                        </>
                      ) : (
                        <>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Property</th>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Investor</th>
                          <th style={{ textAlign: 'right', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Offer Price</th>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${table.headerBorder}`, color: table.headerText, fontWeight: 500, fontSize: '13px' }}>Status</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedMetric === 'submissions' && submissionRecordsRes?.data.map((record, idx) => (
                      <tr key={record.id}>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, textAlign: 'right', color: table.headerText, fontSize: '12px' }}>
                          {(drillDownPage - 1) * pageSize + idx + 1}
                        </td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}` }}>{record.address ?? '-'}</td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, color: table.headerText }}>{record.market ?? '-'}</td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, textTransform: 'capitalize' }}>{record.status}</td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, color: table.headerText }}>
                          {record.submitted_at
                            ? new Date(record.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                            : '-'}
                        </td>
                      </tr>
                    ))}
                    {selectedMetric === 'inquiries' && inquiryRecordsRes?.data.map((record, idx) => (
                      <tr key={record.id}>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, textAlign: 'right', color: table.headerText, fontSize: '12px' }}>
                          {(drillDownPage - 1) * pageSize + idx + 1}
                        </td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}` }}>{record.property_address ?? '-'}</td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, color: table.headerText }}>{record.investor_name ?? '-'}</td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{record.message}</td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, color: table.headerText }}>
                          {new Date(record.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </td>
                      </tr>
                    ))}
                    {selectedMetric === 'offers' && offerRecordsRes?.data.map((record, idx) => (
                      <tr key={record.id}>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, textAlign: 'right', color: table.headerText, fontSize: '12px' }}>
                          {(drillDownPage - 1) * pageSize + idx + 1}
                        </td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}` }}>{record.property_address ?? '-'}</td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, color: table.headerText }}>{record.investor_name ?? '-'}</td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, textAlign: 'right' }}>{formatCurrency(record.offer_price)}</td>
                        <td style={{ padding: '8px', borderBottom: `1px solid ${table.rowBorder}`, textTransform: 'capitalize' }}>{record.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {recordsRes.pagination && (
                  <div className={styles.paginationControls}>
                    <Text variant="bodyXs" color="muted">
                      Rows {((drillDownPage - 1) * pageSize + 1).toLocaleString()}–
                      {Math.min(drillDownPage * pageSize, recordsRes.pagination.total_count).toLocaleString()}
                      {' of '}
                      {recordsRes.pagination.total_count.toLocaleString()}
                    </Text>
                    <Stack direction="row" gap="8">
                      <Button variant="outline" size="sm" disabled={isRecordsFetching || recordsRes.pagination.page <= 1} onClick={() => setDrillDownPage((p) => Math.max(1, p - 1))}>
                        Previous
                      </Button>
                      <Button variant="outline" size="sm" disabled={isRecordsFetching || !recordsRes.pagination.has_more} onClick={() => setDrillDownPage((p) => p + 1)}>
                        Next
                      </Button>
                    </Stack>
                  </div>
                )}
              </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
